/**
 * Subscribes to SignalR workflow execution events and exposes live node
 * statuses, the accumulating state, and an event log for the run panel.
 * Falls back to polling getExecution when SignalR is unavailable.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { signalRService } from '../../services/signalRService';
import { workflowService } from '../../services/workflowService';
import { useSignalR } from '../../providers/SignalRProvider';
import type { NodeRunStatus, RunLogEntry } from '../../types/composer';

export interface WorkflowExecutionStream {
  executionId: string | null;
  status: string | null;
  nodeStatuses: Record<string, NodeRunStatus>;
  state: Record<string, unknown>;
  log: RunLogEntry[];
  errorMessage: string | null;
  isRunning: boolean;
  start: (executionId: string) => Promise<void>;
  stop: () => Promise<void>;
  reset: () => void;
}

const TERMINAL = new Set(['Completed', 'Failed', 'Cancelled']);

let logSeq = 0;
const makeLog = (kind: RunLogEntry['kind'], message: string, nodeId?: string): RunLogEntry => ({
  id: `log-${++logSeq}`,
  at: new Date().toISOString(),
  kind,
  nodeId,
  message,
});

export const useWorkflowExecutionStream = (): WorkflowExecutionStream => {
  const { agentExecutionStatus } = useSignalR();
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [nodeStatuses, setNodeStatuses] = useState<Record<string, NodeRunStatus>>({});
  const [state, setState] = useState<Record<string, unknown>>({});
  const [log, setLog] = useState<RunLogEntry[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const activeIdRef = useRef<string | null>(null);
  const pollTimerRef = useRef<number | null>(null);

  const appendLog = useCallback((entry: RunLogEntry) => {
    setLog((prev) => [...prev.slice(-199), entry]);
  }, []);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const applyStateJson = useCallback((stateJson?: string | null) => {
    if (!stateJson) return;
    try {
      const parsed = JSON.parse(stateJson) as Record<string, unknown>;
      setState(parsed);
    } catch {
      // ignore malformed state
    }
  }, []);

  const stop = useCallback(async () => {
    clearPoll();
    const id = activeIdRef.current;
    if (id) {
      try {
        await signalRService.leaveWorkflowExecution(id);
      } catch {
        // best effort
      }
    }
    activeIdRef.current = null;
  }, [clearPoll]);

  const startPolling = useCallback(
    (id: string) => {
      clearPoll();
      pollTimerRef.current = window.setInterval(async () => {
        try {
          const execution = await workflowService.getExecution(id);
          setStatus(execution.status);
          if (execution.currentNodeId) {
            setNodeStatuses((prev) => ({
              ...prev,
              [execution.currentNodeId!]: prev[execution.currentNodeId!] === 'succeeded'
                ? 'succeeded'
                : 'running',
            }));
          }
          applyStateJson(execution.stateJson);
          if (execution.errorMessage) setErrorMessage(execution.errorMessage);
          if (TERMINAL.has(execution.status)) {
            clearPoll();
            appendLog(makeLog('status', `Execution ${execution.status}`));
          }
        } catch {
          // keep polling; transient failures are fine
        }
      }, 1500);
    },
    [appendLog, applyStateJson, clearPoll],
  );

  const start = useCallback(
    async (id: string) => {
      await stop();
      activeIdRef.current = id;
      setExecutionId(id);
      setStatus('Pending');
      setNodeStatuses({});
      setState({});
      setErrorMessage(null);
      setLog([makeLog('info', `Joined execution ${id}`)]);

      signalRService.onWorkflowEvents({
        onWorkflowNodeStarted: (data) => {
          if (data.executionId !== activeIdRef.current) return;
          setNodeStatuses((prev) => ({ ...prev, [data.nodeId]: 'running' }));
          appendLog(makeLog('node-started', `${data.label || data.nodeId} started (${data.nodeType})`, data.nodeId));
        },
        onWorkflowNodeCompleted: (data) => {
          if (data.executionId !== activeIdRef.current) return;
          setNodeStatuses((prev) => ({ ...prev, [data.nodeId]: 'succeeded' }));
          applyStateJson(data.stateJson);
          appendLog(makeLog('node-completed', `${data.nodeId} completed`, data.nodeId));
        },
        onWorkflowNodeFailed: (data) => {
          if (data.executionId !== activeIdRef.current) return;
          setNodeStatuses((prev) => ({ ...prev, [data.nodeId]: 'failed' }));
          setErrorMessage(data.errorMessage);
          appendLog(makeLog('node-failed', `${data.nodeId} failed: ${data.errorMessage}`, data.nodeId));
        },
        onWorkflowExecutionStatusChanged: (data) => {
          if (data.executionId !== activeIdRef.current) return;
          setStatus(data.status);
          if (data.errorMessage) setErrorMessage(data.errorMessage);
          appendLog(makeLog('status', `Status → ${data.status}`));
          if (TERMINAL.has(data.status)) {
            clearPoll();
          }
        },
      });

      try {
        await signalRService.joinWorkflowExecution(id);
      } catch (err) {
        appendLog(makeLog('error', `SignalR join failed; falling back to polling. ${String(err)}`));
      }

      // Always poll as a safety net — covers missed events and offline SignalR.
      startPolling(id);
    },
    [appendLog, applyStateJson, clearPoll, startPolling, stop],
  );

  const reset = useCallback(() => {
    void stop();
    setExecutionId(null);
    setStatus(null);
    setNodeStatuses({});
    setState({});
    setLog([]);
    setErrorMessage(null);
  }, [stop]);

  useEffect(() => {
    return () => {
      void stop();
      signalRService.offWorkflowEvents();
    };
  }, [stop]);

  // Re-join the execution group after SignalR reconnects.
  useEffect(() => {
    if (agentExecutionStatus === 'connected' && activeIdRef.current) {
      void signalRService.joinWorkflowExecution(activeIdRef.current).catch(() => undefined);
    }
  }, [agentExecutionStatus]);

  return {
    executionId,
    status,
    nodeStatuses,
    state,
    log,
    errorMessage,
    isRunning: status != null && !TERMINAL.has(status),
    start,
    stop,
    reset,
  };
};
